/* Event work gallery: category filter + show more.
   Progressive enhancement only. Without this file every image renders and the
   controls stay hidden, so the section degrades to a plain gallery. */
(() => {
  const norm = (s) => (s || '').trim().toLowerCase();

  function initialise(root = document) {
    root.querySelectorAll('[data-uav-ework]').forEach((section) => {
      if (section.dataset.eworkReady === '1') return;
      section.dataset.eworkReady = '1';

      const grid = section.querySelector('[data-uav-ework-grid]');
      const filters = section.querySelector('[data-uav-ework-filters]');
      const moreWrap = section.querySelector('.uav-ework__more');
      const more = section.querySelector('[data-uav-ework-more]');
      const moreLabel = section.querySelector('[data-uav-ework-more-label]');
      const status = section.querySelector('[data-uav-ework-status]');
      const empty = section.querySelector('[data-uav-ework-empty]');
      if (!grid) return;

      const labels = {
        more: (moreWrap && moreWrap.dataset.moreLabel) || 'More',
        less: (moreWrap && moreWrap.dataset.lessLabel) || 'Less',
        status: (moreWrap && moreWrap.dataset.statusTemplate) || ''
      };

      const items = [...grid.querySelectorAll('.uav-ework__item')];
      const narrow = matchMedia('(max-width: 699px)');
      const desktopLead = Number.parseInt(section.dataset.leadCount, 10) || 6;
      let active = 'all';
      let expanded = false;

      // Only offer a filter that some image actually answers.
      const present = new Set(items.map((i) => norm(i.dataset.category)).filter(Boolean));
      let usable = 0;
      if (filters) {
        filters.querySelectorAll('[data-filter]').forEach((btn) => {
          const key = norm(btn.dataset.filter);
          if (key !== 'all' && !present.has(key)) { btn.remove(); return; }
          usable += 1;
        });
        if (usable > 1) filters.hidden = false;
      }

      const matches = (item) => active === 'all' || norm(item.dataset.category) === active;

      function apply(announce) {
        let shown = 0;
        const lead = narrow.matches ? Math.min(3, desktopLead) : desktopLead;
        section.dataset.filtered = String(active !== 'all');
        items.forEach((item, index) => {
          const ok = matches(item);
          // "More event work" only gates the default view; a filter reveals its own set.
          const gated = !expanded && active === 'all' && index >= lead;
          const visible = ok && !gated;
          item.hidden = !visible;
          if (visible) shown += 1;
        });
        if (empty) empty.hidden = shown !== 0;
        if (more) {
          // The control stays in the tab order in both states. Hiding it on
          // expand is what used to throw keyboard focus back to the top of the
          // document (WCAG 2.4.3), so it now only leaves when there is nothing
          // left for it to do.
          more.hidden = active !== 'all' || items.length <= lead;
          more.setAttribute('aria-expanded', String(expanded));
          if (moreLabel) moreLabel.textContent = expanded ? labels.less : labels.more;
        }
        if (announce && status && labels.status) {
          status.textContent = labels.status
            .replace('%1', String(shown))
            .replace('%2', String(items.length));
        }
      }

      if (filters) {
        filters.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-filter]');
          if (!btn) return;
          active = norm(btn.dataset.filter);
          filters.querySelectorAll('[data-filter]').forEach((b) => {
            b.setAttribute('aria-pressed', String(norm(b.dataset.filter) === active));
          });
          apply(true);
        });
      }

      if (more) {
        more.addEventListener('click', () => {
          expanded = !expanded;
          apply(true);
          // Focus stays on the control the visitor pressed, and the browser
          // keeps it in view when the grid grows or shrinks under it.
          more.focus();
        });
      }

      const onBreakpoint = () => apply(false);
      narrow.addEventListener('change', onBreakpoint);
      const unload = (event) => {
        if (!event.target.contains(section)) return;
        narrow.removeEventListener('change', onBreakpoint);
        document.removeEventListener('shopify:section:unload', unload);
      };
      document.addEventListener('shopify:section:unload', unload);
      apply();
    });
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  initialise();
})();

/* Scroll reveal: draws a white rule under a card the first time it enters view.
   Uses a throttled rect check rather than IntersectionObserver, which does not
   fire in every embedded/offscreen rendering context. Enhancement only: without
   it the card is simply unmarked, never hidden. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  let pending = false;

  const targets = () => [...document.querySelectorAll('[data-uav-reveal]:not(.is-revealed)')];

  function check() {
    pending = false;
    const h = window.innerHeight || document.documentElement.clientHeight;
    for (const el of targets()) {
      const r = el.getBoundingClientRect();
      if (r.height === 0) continue;
      // revealed once a quarter of the card, or 120px of it, has entered the viewport
      const shown = Math.min(r.bottom, h) - Math.max(r.top, 0);
      if (shown >= Math.min(r.height * 0.25, 120)) el.classList.add('is-revealed');
    }
  }

  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(check);
  }

  function start() {
    if (reduce.matches) { targets().forEach((el) => el.classList.add('is-revealed')); return; }
    check();
  }

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
